import { FC, memo } from 'react';

import { isNullish } from 'remeda';
import useSWR from 'swr';

import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';

import css from './GameInfoTooltip.module.css';

import { slotService } from 'v2/api/slotService';
import { Loader } from 'v2/components/loader/Loader';
import { fileSrc } from 'v2/utils';

interface GameInfoTooltipProps {
  providerName: string;
  gameId: string;
}

const GameInfo: FC<GameInfoTooltipProps> = ({ providerName, gameId }) => {
  // Tooltip content mounts on every hover. The global `revalidateOnMount: true` is reset here
  // so a game (or its absence, cached as null) is requested only once.
  const { data: slot, error } = useSWR(['getSlotByIdentifier', providerName, gameId], slotService.getSlotByIdentifier, {
    revalidateOnMount: undefined,
    revalidateIfStale: false,
    shouldRetryOnError: false,
  });

  if (error) {
    return <p className={css.message}>Не удалось загрузить игру</p>;
  }

  if (slot === undefined) {
    return (
      <div className={css.message}>
        <Loader />
      </div>
    );
  }

  if (isNullish(slot)) {
    return <p className={css.message}>Игра не найдена в каталоге</p>;
  }

  return (
    <div className={css.gameInfo}>
      <img className={css.icon} src={fileSrc(slot.iconId)} alt={slot.title} />

      <div className={css.details}>
        <span className={css.title}>{slot.title}</span>
        <span className={css.secondary}>{slot.providerName}</span>
        <span className={css.secondary}>{slot.identifier}</span>

        <Chip
          className={css.status}
          size="small"
          label={slot.isActive ? 'активна' : 'отключена'}
          color={slot.isActive ? 'success' : 'default'}
        />
      </div>
    </div>
  );
};

export const GameInfoTooltip = memo<GameInfoTooltipProps>(({ providerName, gameId }) => (
  <Tooltip
    title={<GameInfo providerName={providerName} gameId={gameId} />}
    placement="right"
    enterDelay={300}
    slotProps={{ tooltip: { className: css.tooltip } }}
  >
    <span className={css.gameId}>{gameId}</span>
  </Tooltip>
));

GameInfoTooltip.displayName = 'GameInfoTooltip';
